import * as React from 'react';
import PageContainer from "@components/PageContainer";
import PageTitle from "@layout/PageTitle";
import Block from "@layout/Block";
import Input from "@layout/Input";
import Textarea from "@layout/Textarea";
import NextButton from "@layout/buttons/Next";
import Font from "@layout/Font";
import classnames from 'classnames';
import ValidatorUtils from '@utils/ValidatorUtils';
import { mobileOperatingSystem } from '@utils/ViewPort';
import './index.scss';

interface Props { }

interface State {
    step: 1 | 2 | 3 | 4;
    name?: string;
    email?: string;
    message?: string;
    error?: string;
}

class ContactPage extends React.Component<Props, State> {
    private nameRef = React.createRef<HTMLInputElement>();
    private emailRef = React.createRef<HTMLInputElement>();
    private messageRef = React.createRef<HTMLTextAreaElement>();
    private buttonWrapperRef = React.createRef<HTMLDivElement>();
    
    readonly DEFAULT_ERROR = 'Please, fill the field before continuing';
    readonly EMAIL_ERROR = 'Invalid email address';
    
    constructor(props: Props) {
        super(props);
        this.state = {
            step: 1,
        }
    }

    componentDidMount() {
        this.nameRef && this.nameRef.current && this.nameRef.current.focus();
    }

    private onKeyDown = (e: React.KeyboardEvent): void => {
        if (13 === e.keyCode) {
            this.nextFieldShow();
        } else if (9 === e.keyCode) {
            this.nextFieldShow();
            e.preventDefault();
            e.stopPropagation();
        }
    }

    private nextFieldShow = () => {
        const { step, name, email, message } = this.state;

        switch(step) {
            case 1:
                if (!name || !name.trim()) {
                    this.setState({error: this.DEFAULT_ERROR});
                    break ;
                }
                this.setState({
                    error: undefined,
                    step: 2,
                });

                setTimeout(() => {
                    this.emailRef && this.emailRef.current && this.emailRef.current.focus();
                }, 1000)
                break ;
            case 2:
                if (!ValidatorUtils.validateEmail(email)) {
                    this.setState({error: this.EMAIL_ERROR});
                    break ;
                }
                this.setState({
                    error: undefined,
                    step: 3,
                });

                setTimeout(() => {
                    this.messageRef && this.messageRef.current && this.messageRef.current.focus();

                    if ('iOS' === mobileOperatingSystem) {
                        return ;
                    }
                    this.buttonWrapperRef &&
                    this.buttonWrapperRef.current &&
                    this.buttonWrapperRef.current.scrollIntoView({ block: 'end', behavior: "smooth" });
                }, 1000);
                break ;
            case 3:
                if (!message || !message.trim()) {
                    this.setState({error: this.DEFAULT_ERROR});
                    break ;
                }

                if (!name || !email || !message) {
                    console.warn('unhandled post case');
                    break ;
                }

                const parameters = [['form-name', 'contact'], ['name', name], ['email', email], ['message', message]]
                    .map(e => encodeURIComponent(e[0]) + "=" + encodeURIComponent(e[1]))
                fetch('/contact', {
                    body: parameters.join('&'),
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    method: "post"
                }).then(() => {
                    this.setState({
                        error: undefined,
                        step: 4,
                    });
                });
            default:
                console.warn('unhandled step case');
        }
    }

    private handleNameChange = (e: React.FormEvent<HTMLInputElement>): void => this.setState({name: e.currentTarget.value});
    private handleEmailChange = (e: React.FormEvent<HTMLInputElement>): void => this.setState({email: e.currentTarget.value});
    private handleMessageChange = (e: React.FormEvent<HTMLTextAreaElement>): void => this.setState({message: e.currentTarget.value});

    render(): JSX.Element {
        const { step } = this.state;
        const labelFontSize = 40;

        return (
            <PageContainer footerHide={true}>
                <Block indent="10">
                    <PageTitle>
                        Contact
                    </PageTitle>
                </Block>
                <Block indent="80">
                    <Font font="secondary">
                        Interested in working together? Please, feel free to drop me a line. If you don't get an answer immediately, I might just be traveling through the middle of nowhere :) I'll get back to you as soon as I can.
                    </Font>
                </Block>

                <form className="contact" name="contact" method="POST" netlify-honeypot="bot-field" data-netlify="true">
					<div className="contact-inner">
						<ul className={classnames('questions', {last: 3 === step })}>
							<li className={classnames('st', {show: 1 === step, hide: 1 !== step})}>
                                <Block indent="40">
                                    <div className="label">
                                        <Font font="secondary" size={labelFontSize}>
                                            <label htmlFor="name">Enter your name</label>
                                        </Font>
                                    </div>
                                </Block>
                                <Input name="name" type="text" placeholder="John Doe" setRef={this.nameRef} onKeyDown={this.onKeyDown} onChange={this.handleNameChange} />
							</li>
							<li className={classnames('nd', {show: 2 === step, hide: 2 < step})}>
                                <Block indent="40">
                                    <div className="label">
                                        <Font font="secondary" size={labelFontSize}>
                                            <label htmlFor="email">Enter your email</label>
                                        </Font>
                                    </div>
                                </Block>
                                <Input name="email" type="email" placeholder="doe.john@gmail.com" setRef={this.emailRef} onKeyDown={this.onKeyDown} onChange={this.handleEmailChange} />
							</li>
							<li className={classnames('rd', {show: 3 === step, hide: 3 < step})}>
                                <Block indent="40">
                                    <div className="label">
                                        <Font font="secondary" size={labelFontSize}>
                                            <label htmlFor="message">Enter your message</label>
                                        </Font>
                                    </div>
                                </Block>
                                <Textarea name="message" placeholder="Enter message here" setRef={this.messageRef} onChange={this.handleMessageChange} />
							</li>
                            <li className={classnames('fth', {show: 4 === step})}>
                                <Block indent="40">
                                    <div className="label">
                                        <Font font="secondary" size={labelFontSize}>
                                            <label htmlFor="message">Thank you! I'll be in touch.</label>
                                        </Font>
                                    </div>
                                </Block>
							</li>
						</ul>
                        <input type="hidden" name="form-name" value="contact" />
                        <p hidden>
                            <label><input name="bot-field" /></label>
                        </p>
                        <div>
                            <span className={classnames('error-message', {show: this.state.error})}>{this.state.error || this.DEFAULT_ERROR}</span>
                        </div>
                        <div ref={this.buttonWrapperRef} className="button-wrapper">
                            {
                                [1, 2].indexOf(step) > -1 && <NextButton onClick={this.nextFieldShow}>Continue</NextButton>
                            }
                            {
                                3 === step && <NextButton hintHide={true} onClick={this.nextFieldShow}>Send</NextButton>
                            }
                        </div>
					</div>
				</form>
            </PageContainer>
        );
    }
}

export default ContactPage;


// WEBPACK FOOTER //
// ./src/components/Contact/index.tsx